/**
 * Single source of truth for analytics events allowed in the Hook platform.
 *
 * Bundles call `useHook().track(event, props)` which validates `event` against
 * this list (sdk/src/track.ts). Trusted code (shell wrapper, SDK auth methods)
 * may bypass the validator but should still only emit events from this list —
 * dashboards and PostHog query schemas are built against it.
 *
 * Adding an event = bump @hook-sdk/sdk minor + PR review. See ADR-011 +
 * ADR-019 + docs/superpowers/specs/2026-04-25-posthog-multi-app-design.md.
 */
export const ALLOWED_EVENTS = [
  // Lifecycle
  'app_opened',
  // Auth funnel
  'signup_started',
  'signup_completed',
  'login_completed',
  // Paywall funnel (bundle)
  'paywall_viewed',
  'paywall_cycle_selected',
  'subscribe_clicked',
  // Paywall conversion-max (template 0.22, ADR-022 amendment 2026-05-12):
  // segmented PIX/Cartão tabs emit `paywall_view` on mount, `*_tab_clicked`
  // on tab switch, `*_cycle_clicked` on cycle change, `*_cta_clicked` on submit.
  // Coexists with legacy `paywall_viewed` + `paywall_cycle_selected` for back-compat.
  'paywall_view',
  'paywall_method_tab_clicked',
  'paywall_cycle_clicked',
  'paywall_cta_clicked',
  // Onboarding + Paywall step tracking — `step` property carries the screen id.
  // Onboarding `step` is app-defined (free string from app.config.json steps[].id);
  // Paywall `step` is template-canonical (see template/src/hooks/usePaywallTracker.ts).
  // Used by Studio dropoff analysis to identify where users churn pre-trial.
  'onboarding_step_viewed',
  'paywall_step_viewed',
  'social_proof_viewed',
  // Billing funnel (backend webhook)
  'trial_started',
  'trial_converted',
  'subscription_canceled',
  'payment_failed',
  'pix_qr_paid',
  'payment_succeeded',
  // Paywall actions (frontend)
  'payment_attempted',
  'unauthenticated_submit_attempted',
  // Payment confirmation (frontend, PaymentReturnHandler)
  'payment_confirmation_started',
  'payment_confirmation_succeeded',
  'payment_confirmation_timed_out',
  // PWA install (see docs/16-pwa-install-prompt.md)
  'pwa_install_splash_shown',
  'pwa_install_splash_accepted',
  'pwa_install_splash_dismissed',
  'pwa_install_prompt_available',
  'pwa_install_prompt_accepted',
  'pwa_install_prompt_dismissed',
  'pwa_installed',
  'pwa_install_error',
  // Service worker (see docs/13-service-worker.md)
  'sw_version_installed',
  // Plan-V — pay-first signup funnel (see docs/superpowers/specs/2026-05-13-pay-first-signup-design.md)
  'checkout_started',
  'checkout_email_taken',
  'checkout_submitted',
  'checkout_payment_failed',
  'access_link_requested',
  'access_link_consumed',
  // P-Video — proprietary VSL player (HookVideo). Emitted by sdk/src/video/HookVideo.tsx
  // and by user code via useHook().track('video_cta_clicked'). Spec:
  // docs/superpowers/specs/2026-05-16-hook-video-player-design.md § Analytics events.
  'video_started',
  'video_resumed',
  'video_paused',
  'video_progress_25',
  'video_progress_50',
  'video_progress_75',
  'video_completed',
  'video_cue_reached',
  'video_cta_clicked',
  'video_continue_watching_restored',
  // SDK 0.28.1 — VideoControls additions.
  'video_unmuted',
  'video_forward_seek_blocked',
  // SDK 0.28.2 — VTurb-parity overhaul (PM-4). Recovery overlay added.
  'video_recovery_shown',
  'video_recovery_dismissed',
  // SDK 0.28.3 — speed control restored as top-right chip (PM-5). Fullscreen
  // remains absent (no UI surface for it).
  'video_speed_changed',
] as const;

export type TrackedEvent = typeof ALLOWED_EVENTS[number];

const ALLOWED_SET: ReadonlySet<string> = new Set(ALLOWED_EVENTS);

export function isAllowedEvent(event: string): event is TrackedEvent {
  return ALLOWED_SET.has(event);
}
